"use client";

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/lib/supabase";
import { isLiveChatEnabledForTier } from "@/lib/live-chat-flag-tier";
import type { DbModelTier } from "@/types/db";

const LiveChatFlagContext = createContext(false);

export function LiveChatFlagProvider({
  allowedModelTiers,
  children,
}: {
  allowedModelTiers: DbModelTier[] | null;
  children: ReactNode;
}) {
  const [userModelTier, setUserModelTier] = useState<DbModelTier>("standard");

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;

      const { data } = await supabase.from("profiles").select("model_tier").eq("user_id", user.id).single();
      if (!cancelled && data) setUserModelTier(data.model_tier ?? "standard");
    })();

    return () => { cancelled = true; };
  }, []);

  const liveChatEnabled = useMemo(
    () => isLiveChatEnabledForTier(allowedModelTiers === null ? null : { model_tiers: allowedModelTiers }, userModelTier),
    [allowedModelTiers, userModelTier]
  );

  return <LiveChatFlagContext.Provider value={liveChatEnabled}>{children}</LiveChatFlagContext.Provider>;
}

export function useLiveChatFlag() {
  return useContext(LiveChatFlagContext);
}
